import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2, Users, UserPlus, Search } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { api, errMsg, LEVELS, SIDES } from "../../lib/api";
import { Avatar, Empty, Field, Loader } from "../common";
import { AthletePicker } from "../AthletePicker";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";

const QuickAthlete = ({ onCreated, level }) => {
  const [f, setF] = useState({ name: "", phone: "", court_side: "" });
  const save = async () => {
    if (!f.name.trim()) return toast.error("Informe o nome");
    try {
      const { data } = await api.post("/athletes", { ...f, level });
      toast.success("Atleta cadastrado");
      setF({ name: "", phone: "", court_side: "" });
      onCreated(data);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="rounded-xl border border-dashed border-[#232B3E] p-3 space-y-2">
      <div className="text-xs font-semibold text-[#94A3B8]">Cadastrar atleta rápido</div>
      <input data-testid="quick-athlete-name" className="inp" placeholder="Nome completo" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
      <div className="grid grid-cols-2 gap-2">
        <input data-testid="quick-athlete-phone" className="inp" placeholder="Telefone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} />
        <select className="inp" value={f.court_side} onChange={(e) => setF({ ...f, court_side: e.target.value })}>
          <option value="">Lado</option>{SIDES.map((s) => <option key={s}>{s}</option>)}
        </select>
      </div>
      <button type="button" data-testid="quick-athlete-save" onClick={save} className="btn-ghost w-full">Cadastrar</button>
    </div>
  );
};

const RegDialog = ({ open, onOpenChange, stage, level, reg, athletes, reloadAthletes, onSaved }) => {
  const { user, isAdmin } = useAuth();
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) setF(reg ? { level: reg.level, player1_id: reg.player1_id, player2_id: reg.player2_id, notes: reg.notes, status: reg.status }
      : { level, player1_id: isAdmin ? "" : user.user_id, player2_id: "", notes: "", status: "confirmada", create_charges: true });
  }, [open, reg, level, isAdmin, user.user_id]);
  const save = async () => {
    if (!f.player1_id || !f.player2_id) return toast.error("Selecione os dois atletas");
    setBusy(true);
    try {
      if (reg) await api.put(`/registrations/${reg.id}`, f);
      else await api.post(`/stages/${stage.id}/registrations`, f);
      toast.success(reg ? "Dupla atualizada" : "Inscrição enviada!");
      onSaved();
      onOpenChange(false);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto border-[#232B3E] bg-[#121722] sm:max-w-md">
        <DialogHeader><DialogTitle className="h-display text-3xl">{reg ? "Editar / substituir dupla" : "Inscrever dupla"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <Field label="Nível">
            <select data-testid="reg-level-select" className="inp" value={f.level || ""} onChange={(e) => setF({ ...f, level: e.target.value })} disabled={!isAdmin && !!reg}>
              {(stage.levels || LEVELS).map((l) => <option key={l}>{l}</option>)}
            </select>
          </Field>
          <Field label="Atleta 1"><AthletePicker testid="reg-player1" athletes={athletes} value={f.player1_id} onChange={(v) => setF({ ...f, player1_id: v })} disabled={!isAdmin} /></Field>
          <Field label="Atleta 2 (parceiro)"><AthletePicker testid="reg-player2" athletes={athletes.filter((a) => a.user_id !== f.player1_id)} value={f.player2_id} onChange={(v) => setF({ ...f, player2_id: v })} /></Field>
          {isAdmin && <QuickAthlete level={f.level} onCreated={async (a) => { await reloadAthletes(); setF((x) => ({ ...x, [x.player1_id ? "player2_id" : "player1_id"]: a.user_id })); }} />}
          {isAdmin && (
            <Field label="Status">
              <select data-testid="reg-status-select" className="inp" value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}>
                <option value="confirmada">Confirmada</option><option value="pendente">Pendente</option><option value="lista_espera">Lista de espera</option>
              </select>
            </Field>
          )}
          <Field label="Observações"><textarea data-testid="reg-notes-input" rows={2} className="inp" value={f.notes || ""} onChange={(e) => setF({ ...f, notes: e.target.value })} /></Field>
          {!reg && stage.price > 0 && (
            <label className="flex items-center gap-2 text-sm text-[#94A3B8]">
              <input type="checkbox" data-testid="reg-create-charges" checked={f.create_charges ?? true} disabled={!isAdmin} onChange={(e) => setF({ ...f, create_charges: e.target.checked })} />
              Gerar cobrança de inscrição para os dois atletas
            </label>
          )}
          <button disabled={busy} data-testid="reg-save-btn" onClick={save} className="btn-lime w-full rounded-full py-3">{busy ? "Salvando..." : "Salvar"}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const STATUS_TXT = { confirmada: "Confirmada", pendente: "Pendente", lista_espera: "Lista de espera" };

const Player = ({ p }) => p ? (
  <Link to={`/app/atletas/${p.user_id}`} className="flex min-w-0 items-center gap-2 hover:text-[#E4F222]">
    <Avatar user={p} size={34} /><div className="min-w-0"><div className="truncate text-sm font-semibold">{p.name}</div><div className="text-[11px] text-[#64748B]">{p.court_side || "—"}</div></div>
  </Link>
) : <span className="text-sm text-[#64748B]">Atleta removido</span>;

export default function RegistrationsTab({ stage, level, athletes, reloadAthletes, onChange }) {
  const { user, isAdmin } = useAuth();
  const [regs, setRegs] = useState(null);
  const [dlg, setDlg] = useState({ open: false, reg: null });
  const [q, setQ] = useState("");
  const load = () => api.get(`/stages/${stage.id}/registrations`, { params: { level } }).then((r) => setRegs(r.data));
  useEffect(() => { setRegs(null); load(); }, [stage.id, level]); // eslint-disable-line react-hooks/exhaustive-deps
  const saved = () => { load(); onChange(); };
  const remove = async (r) => {
    if (!window.confirm(`Excluir a dupla ${r.label}?`)) return;
    try { await api.delete(`/registrations/${r.id}`); toast.success("Dupla excluída"); saved(); } catch (e) { toast.error(errMsg(e)); }
  };
  const canRegister = isAdmin || stage.status === "inscricoes_abertas";
  if (!regs) return <Loader />;
  const shown = regs.filter((r) => r.label.toLowerCase().includes(q.toLowerCase()));
  const mine = regs.some((r) => [r.player1_id, r.player2_id].includes(user.user_id));
  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-3.5 h-4 w-4 text-[#64748B]" />
          <input data-testid="reg-search-input" className="inp pl-9" placeholder="Buscar dupla..." value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        {canRegister && (!mine || isAdmin) && (
          <button data-testid="new-registration-btn" onClick={() => setDlg({ open: true, reg: null })} className="btn-lime flex items-center gap-2 rounded-full px-5 py-2.5 text-sm">
            {isAdmin ? <Plus className="h-4 w-4" /> : <UserPlus className="h-4 w-4" />}{isAdmin ? "Adicionar dupla" : "Inscrever minha dupla"}
          </button>
        )}
      </div>
      {shown.length === 0 ? <Empty icon={Users} title="Sem duplas" text={`Nenhuma dupla inscrita em ${level}.`} /> : (
        <div className="space-y-2">
          {shown.map((r, i) => (
            <div key={r.id} data-testid={`registration-${r.id}`} className="card-x flex flex-wrap items-center gap-3 p-3 sm:flex-nowrap">
              <span className="w-8 font-display text-2xl font-black italic text-[#E4F222]">{i + 1}</span>
              <div className="grid flex-1 grid-cols-1 gap-2 sm:grid-cols-2"><Player p={r.player1} /><Player p={r.player2} /></div>
              <span className="text-[11px] text-[#94A3B8]">{STATUS_TXT[r.status] || r.status}</span>
              {isAdmin && (
                <div className="flex gap-1">
                  <button data-testid={`edit-reg-${r.id}`} onClick={() => setDlg({ open: true, reg: r })} className="rounded-lg p-2 text-[#94A3B8] hover:bg-[#1A202E] hover:text-white"><Pencil className="h-4 w-4" /></button>
                  <button data-testid={`delete-reg-${r.id}`} onClick={() => remove(r)} className="rounded-lg p-2 text-[#94A3B8] hover:bg-[#1A202E] hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <RegDialog open={dlg.open} reg={dlg.reg} onOpenChange={(o) => setDlg({ ...dlg, open: o })} stage={stage} level={level} athletes={athletes} reloadAthletes={reloadAthletes} onSaved={saved} />
    </div>
  );
}
